import axios from 'axios'
import _ from 'lodash'

/**
 * Private methods declarations
*/
const getUsersPage = Symbol('getUsersPage')
const mapUserAttributesValues = Symbol('mapUserAttributesValues')
const getUserAttributes = Symbol('getUserAttributes')

export class Gen3UserService {
  constructor (authorizationStore) {
    this.user = null
    this.authorizationStore = authorizationStore
    this.baseUrl = `${process.env.VUE_APP_API_URL}/user-management`
  }

  async fetchLoggedInUserAttributes () {
    if (this.user) return this.user
    const userAttributes = await this[getUserAttributes]()
    this.user = this[mapUserAttributesValues](userAttributes)
    return this.user
  }

  fetchLoggedInUsername () {
    return this.authorizationStore.currentUser.getUsername()
  }

  /**
     * getUserAttributes array of key:value objects
     *
     * ex.:
     * [
     *  {
     *    "Name": "name",
     *    "Value": "John Doe"
     *  },
     *  {
     *    "Name": "given_name",
     *    "Value": "John"
     *  }
     * ]
     * @return {Promise<Array<Object>>}
     */
  async [getUserAttributes] () {
    return this.authorizationStore.currentUser.getUserAttributesAsync()
  }

  /**
   * mapUserAttributesValues converts the user array into a key:value object for the user's attributes
   * @param {Array} user array from getUserAttributesAsync
   * ex.:
   * {
   *  "sub":"dab978bd-a382-481c-9dce-r4nd0m",
   *  "email_verified":"false",
   *  "name":"John Doe",
   *  "given_name":"John",
   *  "family_name":"Doe",
   *  "email":"john.doe@britecore.com"
   * }
   * @return {Object}
   */
  [mapUserAttributesValues] (userAttributes) {
    return _.mapValues(_.keyBy(userAttributes, 'Name'), 'Value')
  }

  async fetchAllUsers (next = null) {
    // This filter is used to skip user dirty data in engineering-dev where each BC username has been migrated from
    // OKTA_xxx@britecore.com to britecore_xxx@britecore.com format.
    const removeDirtyUser = (users) => {
      if (_.isEmpty(users)) {
        return users
      }
      return users.filter((user) => {
        return !(user.username.startsWith('OKTA_') && user.username.endsWith('@britecore.com'))
      })
    }

    const response = await this[getUsersPage](next)
    const users = removeDirtyUser(response.users)
    if (response.next) {
      return _.unionBy(users, await this.fetchAllUsers(response.next), 'username')
    }
    return users
  }

  async [getUsersPage] (cursor) {
    let encodedURICursor = ''
    if (cursor) {
      encodedURICursor = `&cursor=${encodeURIComponent(cursor)}`
    }
    const maxPageSize = 60 // max page size supported by BriteAuth
    const url = `users?limit=${maxPageSize}${encodedURICursor}`
    const response = await axios.get(`${this.baseUrl}/${url}`, null)
    return response.data
  }
}

export class Gen2UserService {
  constructor () {
    this.user = null
  }

  // Gen2 doesn't need this information for now
  // since the API is responsible for filling user related data
  fetchLoggedInUsername () {
    return ({
      username: ''
    })
  }

  // Gen2 doesn't need this information for now
  // since the API is responsible for filling user related data
  async fetchLoggedInUserAttributes () {
    return ({
      name: ''
    })
  }
}
